'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import {
  decodeHtmlEntities,
  extractIframeSrc,
  getEmbedUrl,
  sanitizeEmbedHtml,
} from '@/lib/video/embed'

type Props = {
  url?: string | null
  html?: string | null
  className?: string
  frameClassName?: string
  rounded?: 'xl' | '2xl' | '3xl'
  variant?: 'plain' | 'glass'
}

export function VideoEmbed({
  url,
  html,
  className,
  frameClassName,
  rounded = '2xl',
  variant = 'glass',
}: Props) {
  const [loaded, setLoaded] = useState(false)

  const { src } = useMemo(() => {
    if (url && String(url).trim()) {
      return { src: getEmbedUrl(url) }
    }

    const decoded = html ? decodeHtmlEntities(String(html)) : ''
    const safe = sanitizeEmbedHtml(decoded)
    const iframeSrc = extractIframeSrc(safe)

    return { src: iframeSrc ? getEmbedUrl(iframeSrc) : '' }
  }, [url, html])

  useEffect(() => {
    setLoaded(false)
  }, [src])

  if (!src) return null

  const roundedClass =
    rounded === '3xl' ? 'rounded-3xl' : rounded === 'xl' ? 'rounded-xl' : 'rounded-2xl'

  return (
    <div
      className={cn(
        'relative w-full aspect-video overflow-hidden',
        roundedClass,
        variant === 'glass' ? 'border border-white/15 bg-white/5' : 'bg-muted',
        className
      )}
    >
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-muted">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-12 w-12 rounded-full bg-background/70" />
          </div>
        </div>
      )}

      <iframe
        src={src}
        className={cn(
          'absolute inset-0 block h-full w-full border-0 transition-opacity duration-300',
          loaded ? 'opacity-100' : 'opacity-0',
          frameClassName
        )}
        allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        onLoad={() => setLoaded(true)}
      />
    </div>
  )
}